import React, { useEffect, useState, useCallback } from 'react';
import { List, message, Pagination, Skeleton, Space } from 'antd';
import { useTranslation } from 'react-i18next';
import NewInternshipComponent from './NewInternshipComponent';
import UpdateInternshipComponent from './UpdateInternshipComponent';
import { GetInternshipOffersOfOrganization } from '../../helper/apiHelper';
import type { ReducedInternship } from '../../Types/interfaces';
import { useUserOrganizations } from '../../hooks/useOrganizations';
import { useCurrentUser } from '../../hooks/useCurrentUser';

const InternshipOffersComponent = (): React.ReactElement => {
  const { t } = useTranslation();
  const loggedInUser = useCurrentUser();
  const [internships, setInternships] = useState<ReducedInternship[]>([]);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState(1);
  const start = (currentPage - 1) * itemsPerPage;
  const end = Math.min(start + itemsPerPage, internships.length);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { data: userOrganizations } = useUserOrganizations(loggedInUser?.id);

  const reloadInternShipComponentList = useCallback(async () => {
    try {
      const internshipResps: ReducedInternship[] = [];

      await Promise.all(
        userOrganizations?.map(async (org) => {
          const resp = await GetInternshipOffersOfOrganization(
            org.id.toString()
          );
          internshipResps.push(...resp);
        }) ?? []
      );

      setInternships(internshipResps);
    } catch (error) {
      console.error(error);
      message.error(t('internshipOffersComp.fetchError'));
    } finally {
      setIsLoading(false);
    }
  }, [userOrganizations, t]);

  useEffect(() => {
    const getInternships = async (): Promise<void> => {
      reloadInternShipComponentList();
    };
    getInternships();
  }, [reloadInternShipComponentList]);

  const isUnderControl = (item: ReducedInternship): boolean => {
    return (
      userOrganizations?.some((org) => org.id === item.organizationId) ?? false
    );
  };

  const showPage = (pageNumber: number): void => {
    setCurrentPage(pageNumber);
  };

  const SkeletonList = (
    <List
      dataSource={[1, 2, 3, 4, 5, 6, 7, 8]}
      style={{ margin: '5px' }}
      renderItem={() => {
        return (
          <List.Item>
            <Skeleton
              active
              paragraph={{ rows: 2, width: ['50%', '80%'] }}
              title={false}
            />
          </List.Item>
        );
      }}
    ></List>
  );

  return (
    <>
      <br />
      <Space direction="vertical" style={{ display: 'flex' }} align="end">
        <NewInternshipComponent
          onNewInternShipComponentAdded={reloadInternShipComponentList}
        />
      </Space>
      {isLoading ? (
        SkeletonList
      ) : (
        <List
          itemLayout="horizontal"
          dataSource={internships.slice(start, end)}
          renderItem={(item) => {
            if (isUnderControl(item)) {
              return (
                <List.Item
                  key={item.id}
                  actions={[
                    <UpdateInternshipComponent
                      key={`${item.id}-update`}
                      id={item.id}
                      onInternShipComponentUpdated={
                        reloadInternShipComponentList
                      }
                    />,
                  ]}
                >
                  <List.Item.Meta
                    title={item.name}
                    description={item.description}
                  />
                </List.Item>
              );
            } else {
              return null;
            }
          }}
        />
      )}
      <Space
        direction="vertical"
        size="middle"
        style={{ display: 'flex' }}
        align="center"
      >
        <Pagination
          responsive
          pageSize={itemsPerPage}
          current={currentPage}
          total={internships.length}
          onChange={showPage}
          showSizeChanger={true}
          pageSizeOptions={['5', '10', '15', '30', '50', '100', '200']}
          onShowSizeChange={(current, size) => {
            setItemsPerPage(size);
          }}
        />
      </Space>
    </>
  );
};

export default InternshipOffersComponent;
